import { useState } from "react";
import { Lightning, CaretDown, CaretUp, Warning } from "@phosphor-icons/react";
import { useTheme } from "../../context/ThemeContext";
import { PlatformCard } from "./PlatformCard";

export function ExecuteNowCard({ guide }) {
  const [expanded, setExpanded] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  if (!guide) return null;

  const platformGroups = guide.platforms
    ? Object.entries(
        guide.platforms.reduce((acc, p) => {
          const region = p.region || "Global";
          if (!acc[region]) acc[region] = [];
          acc[region].push(p);
          return acc;
        }, {})
      )
    : [];

  return (
    <div
      className={`rounded-xl border overflow-hidden mb-6 ${
        isDark
          ? "bg-gradient-to-r from-purple-500/5 to-emerald-500/5 border-purple-500/20"
          : "bg-gradient-to-r from-purple-50 to-emerald-50 border-purple-200"
      }`}
      data-testid="execute-now-card"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-4 ${
          isDark ? "hover:bg-white/[0.02]" : "hover:bg-purple-50/50"
        }`}
        data-testid="execute-now-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
            <Lightning size={22} className="text-white" weight="fill" />
          </div>
          <div className="text-left">
            <h3 className={`font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Execute Now
            </h3>
            <p className={`text-xs font-body ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {guide.platforms?.length || 0} platforms &bull; {platformGroups.length} regions
            </p>
          </div>
        </div>
        {expanded ? (
          <CaretUp size={20} className={isDark ? "text-gray-500" : "text-gray-400"} />
        ) : (
          <CaretDown size={20} className={isDark ? "text-gray-500" : "text-gray-400"} />
        )}
      </button>

      {expanded && (
        <div className={`p-4 pt-0 animate-fade-in space-y-4 border-t ${isDark ? "border-white/10" : "border-purple-100"}`}>
          {/* Prerequisites */}
          {guide.prerequisites?.length > 0 && (
            <div className="pt-4">
              <h4 className={`text-sm font-bold mb-2 flex items-center gap-1 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                <Warning size={14} />Prerequisites
              </h4>
              <ul className="space-y-1">
                {guide.prerequisites.map((prereq) => (
                  <li key={prereq} className={`text-xs font-body ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    &bull; {prereq}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Platform groups by region */}
          {platformGroups.map(([region, platforms]) => (
            <div key={region}>
              <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                {region}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {platforms.map((platform) => (
                  <PlatformCard key={platform.name} platform={platform} />
                ))}
              </div>
            </div>
          ))}

          {/* Estimated Costs */}
          {guide.estimated_costs && (
            <div className={`text-xs font-body mt-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              Est. costs: {guide.estimated_costs}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
